import { useEffect, useRef } from 'react';

interface PageAutoRefreshOptions {
  enabled?: boolean;
  intervalMs?: number;
}

/**
 * Refreshes a live business view when the operator returns to it and,
 * optionally, on a visible-tab interval. It deliberately does not fire on
 * mount; the page keeps ownership of its initial loading state.
 */
export function usePageAutoRefresh(
  refresh: () => void | Promise<void>,
  { enabled = true, intervalMs }: PageAutoRefreshOptions = {},
) {
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let inFlight = false;
    let wasHidden = document.visibilityState === 'hidden';
    let lastRunAt = 0;

    const run = async () => {
      const now = Date.now();
      if (
        cancelled
        || inFlight
        || document.visibilityState !== 'visible'
        || now - lastRunAt < 1000
      ) {
        return;
      }

      inFlight = true;
      lastRunAt = now;
      try {
        await refreshRef.current();
      } finally {
        inFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasHidden = true;
        return;
      }

      if (wasHidden) {
        wasHidden = false;
        void run();
      }
    };

    const onFocus = () => {
      void run();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    const intervalId = intervalMs && intervalMs > 0
      ? window.setInterval(() => { void run(); }, intervalMs)
      : null;

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      if (intervalId !== null) window.clearInterval(intervalId);
    };
  }, [enabled, intervalMs]);
}
